import type {ReactElement} from 'react';
import React, { useRef} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconType} from '@Components/icon-v2/icon.types';
import styles from './masking-image-container.module.scss';
import {useAppSelector} from '@/hooks';

export interface MaskingImageContainerProps {
  isSmall: boolean;
  children?: ReactElement;
  additionalClasses?: string;
  undoRedoCallback?: (actionType: EditAction) => void;
  zoomCallback?: (zoomType: ZoomType) => void;
}

export enum ZoomType {
  ZOOM_IN,
  ZOOM_OUT,
}

export enum EditAction {
  UNDO,
  REDO,
}

function MaskingImageContainer({...props}: MaskingImageContainerProps): ReactElement {
  const allowUndoRedo = useRef(typeof props.undoRedoCallback !== 'undefined');
  const allowZoom = useRef(typeof props.zoomCallback !== 'undefined');
  const disableZoomOut = useAppSelector((state) => {
    return state.freehandMasking.disableZoomOut;
  });
  const disableZoomIn = useAppSelector((state) => {
    return state.freehandMasking.disableZoomIn;
  });
  const disableUndo = useAppSelector((state) => {
    return state.freehandMasking.disableUndo;
  });
  const disableRedo = useAppSelector((state) => {
    return state.freehandMasking.disableRedo;
  });

  const getSmallScreenUndoRedoPanel = (): ReactElement => {
    return (
      <div className="_full-height flex-row-align-center">
        <Icon
          onClick={(): void => {
            if (props.undoRedoCallback) {
              props.undoRedoCallback(EditAction.UNDO);
            }
          }}
          icon="icon-undo"
          type={IconType.GHOST}
          className={`${styles.smallPanelIcon} _full-height spacing-p-r-4 spacing-p-l-4 flex-center`}
        />
        <Icon
          onClick={(): void => {
            if (props.undoRedoCallback) {
              props.undoRedoCallback(EditAction.REDO);
            }
          }}
          icon="icon-redo"
          type={IconType.GHOST}
          className={`${styles.smallPanelIcon} _full-height spacing-p-r-4 spacing-p-l-4 flex-center`}
        />
      </div>
    );
  };

  const getSmallScreenZoomPanel = (): ReactElement => {
    return (
      <div className="_full-height flex-row-align-center">
        <Icon
          onClick={(): void => {
            if (props.zoomCallback) {
              props.zoomCallback(ZoomType.ZOOM_IN);
            }
          }}
          isDisabled={disableZoomIn}
          icon="icon-zoom-in"
          type={IconType.GHOST}
          className={`${styles.smallPanelIcon} _full-height spacing-p-r-4 spacing-p-l-4 flex-center`}
        />
        <Icon
          onClick={(): void => {
            if (props.zoomCallback) {
              props.zoomCallback(ZoomType.ZOOM_OUT);
            }
          }}
          isDisabled={disableZoomOut}
          icon="icon-zoom-out"
          type={IconType.GHOST}
          className={`${styles.smallPanelIcon} _full-height spacing-p-r-4 spacing-p-l-4 flex-center`}
        />
      </div>
    );
  };

  const getUndoRedoPanel = (isSmallScreen = false): ReactElement => {
    if (isSmallScreen) {
      return getSmallScreenUndoRedoPanel();
    }
    return (
      <div className={`${styles.floatingContainer} radius-4 flexbox spacing-p-r-2 spacing-p-l-2`}>
        <Icon
          isDisabled={disableUndo}
          onClick={(): void => {
            if (props.undoRedoCallback) {
              props.undoRedoCallback(EditAction.UNDO);
            }
          }}
          icon="icon-undo"
          type={IconType.GHOST}
          className={`spacing-p-t-2 spacing-p-r-1 spacing-p-b-2 spacing-p-l-1 ${styles.panelIcon}`}
          shape={IconShape.SQUARE}
          isUsedAsButton
        />
        <Icon
          isDisabled={disableRedo}
          onClick={(): void => {
            if (props.undoRedoCallback) {
              props.undoRedoCallback(EditAction.REDO);
            }
          }}
          icon="icon-redo"
          type={IconType.GHOST}
          className={`spacing-p-t-2 spacing-p-r-1 spacing-p-b-2 spacing-p-l-1 ${styles.panelIcon}`}
          shape={IconShape.SQUARE}
          isUsedAsButton
        />
      </div>
    );
  };

  const getZoomPanel = (undoRedoPanel = false, isSmallScreen = false): ReactElement => {
    if (isSmallScreen) {
      return getSmallScreenZoomPanel();
    }
    return (
      <div className={`${styles.floatingContainer} ${undoRedoPanel ? styles.pushToRight : ''} radius-4 flexbox spacing-p-r-2 spacing-p-l-2`}>
        <Icon
          isDisabled={disableZoomIn}
          onClick={(): void => {
            if (props.zoomCallback) {
              props.zoomCallback(ZoomType.ZOOM_IN);
            }
          }}
          icon="icon-zoom-in"
          type={IconType.GHOST}
          className={`spacing-p-t-2 spacing-p-r-1 spacing-p-b-2 spacing-p-l-1 ${styles.panelIcon}`}
          shape={IconShape.SQUARE}
          isUsedAsButton
        />
        <Icon
          isDisabled={disableZoomOut}
          onClick={(): void => {
            if (props.zoomCallback) {
              props.zoomCallback(ZoomType.ZOOM_OUT);
            }
          }}
          icon="icon-zoom-out"
          type={IconType.GHOST}
          className={`spacing-p-t-2 spacing-p-r-1 spacing-p-b-2 spacing-p-l-1 ${styles.panelIcon}`}
          shape={IconShape.SQUARE}
          isUsedAsButton
        />
      </div>
    );
  };

  const getContainer = (isZoom = false, isUndoRedo = false): ReactElement => {
    return (
      <div className={`_full-width _full-height ${styles.parentContainer}`}>
        {isZoom || isUndoRedo ? (
          <div className={`${styles.subPanelContainer} _full-width flex-row-align-center flex-justify-between`}>
            {isUndoRedo ? getUndoRedoPanel() : null}
            {isZoom ? getZoomPanel(isUndoRedo) : null}
          </div>
        ) : null}
        <div className={`${styles.subContainer} ${props.additionalClasses ?? ''} _full-width _full-height flex-center flex-v-row`}>{props.children}</div>
      </div>
    );
  };

  const getContainerForSmallScreen = (isZoom = false, isUndoRedo = false): ReactElement => {
    return (
      <div className={`${styles.smallSubContainer} ${props.additionalClasses ?? ''} _full-width _full-height flexbox radius-2 border-s-dark`}>
        {isZoom || isUndoRedo ? (
          <div className={`${styles.smallRow} flex-row-align-center flex-justify-between`}>
            {isUndoRedo ? getUndoRedoPanel(true) : null}
            {isZoom ? getZoomPanel(isUndoRedo, true) : null}
          </div>
        ) : null}
        <div className={`${styles.smallImageContainer} flex-center`}>{props.children}</div>
      </div>
    );
  };

  return props.isSmall ? getContainerForSmallScreen(allowZoom.current, allowUndoRedo.current) : getContainer(allowZoom.current, allowUndoRedo.current);
}

export default MaskingImageContainer;
